import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { fmtDDMMYYYY } from '../lib/format';
import CrmModal from '../clientModule/components/CrmModal';
import {
  IconUsers,
  IconZap,
  IconClock,
  IconFilm,
  IconImage,
  IconFileText,
  IconRenewal,

  IconAlertTriangle,
  IconCalendar,

  IconSparkles,
  IconCheck
} from '../clientModule/components/SvgIcons';

export default function StaffDashboard({ user }) {
  const isStaff = user?.role === 'staff';
  const [clients, setClients] = useState([]);
  const [renewalsData, setRenewalsData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState(null); // 'clients' | 'active_services' | 'pending_all' | 'pending_reels' | 'pending_posts' | 'other_tasks'
  const [modalSearch, setModalSearch] = useState('');
  const [trackerFilter, setTrackerFilter] = useState('all'); // 'all' | 'critical' | 'upcoming'

  const loadData = () => {
    setLoading(true);
    Promise.all([
      api.get('/api/client-module/clients').catch(() => []),
      api.get('/api/client-module/renewals').catch(() => null),
      api.get('/api/staff-module/tasks').catch(() => []),
    ])
      .then(([clientsRes, renewalsRes, tasksRes]) => {
        setClients(clientsRes || []);
        setRenewalsData(renewalsRes || null);
        setTasks(tasksRes || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
    const timer = setInterval(loadData, 15000);
    return () => clearInterval(timer);
  }, []);

  const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0);

  // 1. Total Clients
  const totalClientsCount = clients.length;

  // Gather all service orders across clients
  const allOrders = [];
  clients.forEach((cli) => {
    const orders = cli.serviceOrders || [];
    orders.forEach((ord) => {
      allOrders.push({
        ...ord,
        clientId: cli.id,
        clientName: cli.clientName,
        company: cli.company || '',
        mobile: cli.mobile || '',
      });
    });
  });

  // 2. Total Active Services
  const activeOrders = allOrders.filter((ord) => ord.workStatus !== 'Cancelled');
  const totalActiveServicesCount = activeOrders.length;

  // 3. Pending All Work
  const pendingOrders = activeOrders.filter((ord) => ord.workStatus !== 'Completed');
  const pendingTasksList = tasks.filter((t) => t.status !== 'Completed' && t.status !== 'Cancelled');
  const totalPendingWorkCount = pendingOrders.length > 0 ? pendingOrders.length : pendingTasksList.length;

  // Helpers to classify Reel vs Post vs Other
  const isReelItem = (item) => {
    const name = String(item.serviceName || item.taskName || '').toLowerCase();
    const unit = String(item.unit || '').toLowerCase();
    return name.includes('reel') || unit.includes('reel');
  };

  const isPostItem = (item) => {
    const name = String(item.serviceName || item.taskName || '').toLowerCase();
    const unit = String(item.unit || '').toLowerCase();
    return (
      name.includes('post') ||
      name.includes('graphic') ||
      name.includes('creative') ||
      name.includes('banner') ||
      name.includes('design') ||
      unit.includes('post') ||
      unit.includes('graphic') ||
      unit.includes('creative') ||
      unit.includes('banner')
    );
  };

  // 4. Pending Reels
  const pendingReelsOrders = pendingOrders.filter(isReelItem);
  const pendingReelsTasks = pendingTasksList.filter(isReelItem);
  const totalPendingReelsQty = pendingReelsOrders.reduce((sum, ord) => {
    const qty = Number(ord.quantity) || 1;
    const completed = Number(ord.completedQty) || 0;
    return sum + Math.max(1, qty - completed);
  }, 0) || pendingReelsTasks.length;

  // 5. Pending Posts
  const pendingPostsOrders = pendingOrders.filter(isPostItem);
  const pendingPostsTasks = pendingTasksList.filter(isPostItem);
  const totalPendingPostsQty = pendingPostsOrders.reduce((sum, ord) => {
    const qty = Number(ord.quantity) || 1;
    const completed = Number(ord.completedQty) || 0;
    return sum + Math.max(1, qty - completed);
  }, 0) || pendingPostsTasks.length;

  // 6. All Other Tasks
  const otherPendingOrders = pendingOrders.filter((ord) => !isReelItem(ord) && !isPostItem(ord));
  const otherPendingTasks = pendingTasksList.filter((t) => !isReelItem(t) && !isPostItem(t));
  const totalOtherTasksCount = otherPendingOrders.length > 0 ? otherPendingOrders.length : otherPendingTasks.length;

  // 7. Service Renewal & Expiration Tracker Items
  const trackerItems = [];
  
  if (renewalsData) {
    const categories = ['overdue', 'today', 'tomorrow', 'next7Days', 'next30Days', 'future'];
    categories.forEach((cat) => {
      const items = renewalsData[cat] || [];
      items.forEach((item) => {
        trackerItems.push({
          clientId: item.clientId,
          clientName: item.clientName,
          company: item.company || '',
          serviceName: item.serviceName,
          quantity: item.quantity || 1,
          unit: item.unit || '',
          renewalDate: item.renewalDate,
          category: cat,
        });
      });
    });
  } else {
    // Fallback directly from activeOrders
    activeOrders.forEach((ord) => {
      if (ord.renewalDate || ord.endDate) {
        trackerItems.push({
          clientId: ord.clientId,
          clientName: ord.clientName,
          company: ord.company || '',
          serviceName: ord.serviceName,
          quantity: ord.quantity || 1,
          unit: ord.unit || '',
          renewalDate: ord.renewalDate || ord.endDate,
        });
      }
    });
  }

  // Format Renewal Status String helper
  const getExpirationDetails = (rDateStr) => {
    if (!rDateStr) return { text: 'no renewal date set', level: 'normal', badge: 'Standard' };
    const rDate = new Date(rDateStr);
    rDate.setHours(0, 0, 0, 0);
    const diffDays = Math.round((rDate.getTime() - todayDate.getTime()) / (86400 * 1000));

    if (diffDays < 0) {
      const abs = Math.abs(diffDays);
      const text = abs === 1 ? 'expired yesterday' : `expired ${abs} days ago`;
      return { text, level: 'critical', badge: `Expired (${fmtDDMMYYYY(rDateStr)})`, diffDays };
    } else if (diffDays === 0) {
      return { text: 'expire today', level: 'warning', badge: `Expire Today (${fmtDDMMYYYY(rDateStr)})`, diffDays };
    } else if (diffDays === 1) {
      return { text: 'expire tomorrow', level: 'warning', badge: `Expire Tomorrow (${fmtDDMMYYYY(rDateStr)})`, diffDays };
    } else {
      return { text: `renews in ${diffDays} days`, level: 'upcoming', badge: `Renews ${fmtDDMMYYYY(rDateStr)}`, diffDays };
    }
  };

  // Sort tracker items by diffDays (Overdue & Soonest at top)
  const processedTrackerItems = trackerItems.map((item) => {
    const exp = getExpirationDetails(item.renewalDate);
    const qtyUnit = item.quantity > 1 ? `${item.quantity} ${item.unit || item.serviceName}` : item.serviceName;
    const clientFullName = `${item.clientName}${item.company ? ' ' + item.company : ''}`.trim();
    const infoSentence = `${clientFullName} ${qtyUnit} ${exp.text}`;

    return {
      ...item,
      exp,
      qtyUnit,
      clientFullName,
      infoSentence,
    };
  });

  processedTrackerItems.sort((a, b) => (a.exp.diffDays ?? 999) - (b.exp.diffDays ?? 999));

  const filteredTrackerItems = processedTrackerItems.filter((item) => {
    if (trackerFilter === 'critical') return item.exp.level === 'critical' || item.exp.level === 'warning';
    if (trackerFilter === 'upcoming') return item.exp.level === 'upcoming';
    return true;
  });

  const openCardModal = (type) => {
    if (isStaff && (type === 'clients' || type === 'active_services')) return;
    setModalSearch('');
    setActiveModal(type);
  };

  return (
    <div style={{ width: '100%', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif" }}>
      {/* ── Full Width Hero Mesh Gradient Banner ── */}
      <div
        style={{
          background: 'linear-gradient(135deg, #022c22 0%, #0f172a 45%, #224eae 100%)',
          borderRadius: 24,
          padding: '32px 38px',
          color: '#ffffff',
          marginBottom: 32,
          boxShadow: '0 20px 40px -15px rgba(2, 44, 34, 0.4)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 20,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle Decorative Background Glow */}
        <div style={{ position: 'absolute', top: '-50%', right: '-10%', width: 400, height: 400, background: 'radial-gradient(circle, rgba(52,211,153,0.15) 0%, rgba(0,0,0,0) 70%)', pointerEvents: 'none' }} />

        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)', padding: '6px 16px', borderRadius: 30, fontSize: '0.82rem', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 12, border: '1px solid rgba(255,255,255,0.18)' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--brand)', display: 'inline-block' }} className="cm-live-dot" />
            <IconSparkles size={14} color="#34d399" /> Live Operations Dashboard
          </div>
          <h1 style={{ margin: 0, fontWeight: 900, fontSize: '2.1rem', letterSpacing: '-0.03em', color: '#ffffff' }}>
            Welcome back, {user?.name || user?.username || 'Team'}
          </h1>
          <p style={{ margin: '8px 0 0', opacity: 0.9, fontSize: '1rem', maxWidth: 720, lineHeight: 1.5 }}>
            Real-time business summary synchronized automatically from Google Sheets & CRM. Click any stat card below to open its full list.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {!isStaff && (
            <>
              <div style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)', padding: '14px 22px', borderRadius: 16, border: '1px solid rgba(255,255,255,0.15)' }}>
                <div style={{ fontSize: '0.75rem', opacity: 0.75, textTransform: 'uppercase', fontWeight: 800, letterSpacing: '0.04em' }}>Clients</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 900, color: '#ffffff', marginTop: 2 }}>{totalClientsCount}</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)', padding: '14px 22px', borderRadius: 16, border: '1px solid rgba(255,255,255,0.15)' }}>
                <div style={{ fontSize: '0.75rem', opacity: 0.75, textTransform: 'uppercase', fontWeight: 800, letterSpacing: '0.04em' }}>Active Services</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 900, color: '#ffffff', marginTop: 2 }}>{totalActiveServicesCount}</div>
              </div>
            </>
          )}
          <div style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)', padding: '14px 22px', borderRadius: 16, border: '1px solid rgba(255,255,255,0.15)' }}>
            <div style={{ fontSize: '0.75rem', opacity: 0.75, textTransform: 'uppercase', fontWeight: 800, letterSpacing: '0.04em' }}>Pending Work</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 900, color: '#ffffff', marginTop: 2 }}>{totalPendingWorkCount}</div>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 80, textAlign: 'center', color: 'var(--muted)', fontSize: '1.2rem', fontWeight: 700 }}>
          <IconClock size={24} /> Loading live statistics from Google Sheets…
        </div>
      ) : (
        <>
          {/* ── 6 Main Clickable Stat Cards Grid (Full Width) ── */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
              gap: 20,
              marginBottom: 36,
            }}
          >
            {/* 1. Total Clients Card Button */}
            {!isStaff && (
              <div
                onClick={() => openCardModal('clients')}
                style={{
                  background: 'var(--card)',
                  borderRadius: 20,
                  padding: '24px 26px',
                  border: '1px solid var(--line)',
                  boxShadow: '0 10px 25px -5px rgba(0,0,0,0.04)',
                  borderTop: '5px solid #224eae',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
                className="cm-stat-clickable-card"
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>TOTAL CLIENTS</span>
                    <span style={{ background: 'var(--chip-bg)', padding: '10px', borderRadius: 14, border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <IconUsers size={22} color="#224eae" />
                    </span>
                  </div>
                  <div style={{ fontSize: '2.8rem', fontWeight: 900, color: 'var(--ink)', lineHeight: 1 }}>{totalClientsCount}</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 600 }}>Registered clients</span>
                  <span style={{ fontSize: '0.78rem', color: 'var(--brand)', fontWeight: 900, background: 'var(--chip-bg)', padding: '4px 10px', borderRadius: 20 }}>View List →</span>
                </div>
              </div>
            )}

            {/* 2. Total Active Services Card Button */}
            {!isStaff && (
              <div
                onClick={() => openCardModal('active_services')}
                style={{
                  background: 'var(--card)',
                  borderRadius: 20,
                  padding: '24px 26px',
                  border: '1px solid var(--line)',
                  boxShadow: '0 10px 25px -5px rgba(0,0,0,0.04)',
                  borderTop: '5px solid #224eae',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
                className="cm-stat-clickable-card"
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>ACTIVE SERVICES</span>
                    <span style={{ background: 'var(--chip-bg)', padding: '10px', borderRadius: 14, border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <IconZap size={22} color="#224eae" />
                    </span>
                  </div>
                  <div style={{ fontSize: '2.8rem', fontWeight: 900, color: 'var(--ink)', lineHeight: 1 }}>{totalActiveServicesCount}</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 600 }}>Ongoing orders</span>
                  <span style={{ fontSize: '0.78rem', color: 'var(--brand)', fontWeight: 900, background: 'var(--chip-bg)', padding: '4px 10px', borderRadius: 20 }}>View List →</span>
                </div>
              </div>
            )}

            {/* 3. Pending All Work Card Button */}
            <div
              onClick={() => openCardModal('pending_all')}
              style={{
                background: 'var(--card)',
                borderRadius: 20,
                padding: '24px 26px',
                border: '1px solid var(--line)',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.04)',
                borderTop: '5px solid #0f172a',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
              className="cm-stat-clickable-card"
            >
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <span style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>PENDING ALL WORK</span>
                  <span style={{ background: 'var(--bg)', padding: '10px', borderRadius: 14, border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconClock size={22} color="#0f172a" />
                  </span>
                </div>
                <div style={{ fontSize: '2.8rem', fontWeight: 900, color: 'var(--ink)', lineHeight: 1 }}>{totalPendingWorkCount}</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 600 }}>Work in progress</span>
                <span style={{ fontSize: '0.78rem', color: 'var(--ink)', fontWeight: 900, background: 'var(--bg)', padding: '4px 10px', borderRadius: 20 }}>View List →</span>
              </div>
            </div>

            {/* 4. Total Pending Reels Card Button */}
            <div
              onClick={() => openCardModal('pending_reels')}
              style={{
                background: 'var(--card)',
                borderRadius: 20,
                padding: '24px 26px',
                border: '1px solid var(--line)',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.04)',
                borderTop: '5px solid #224eae',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
              className="cm-stat-clickable-card"
            >
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <span style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>PENDING REELS</span>
                  <span style={{ background: 'var(--chip-bg)', padding: '10px', borderRadius: 14, border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconFilm size={22} color="#224eae" />
                  </span>
                </div>
                <div style={{ fontSize: '2.8rem', fontWeight: 900, color: 'var(--ink)', lineHeight: 1 }}>{totalPendingReelsQty}</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 600 }}>Reels to publish</span>
                <span style={{ fontSize: '0.78rem', color: 'var(--brand)', fontWeight: 900, background: 'var(--chip-bg)', padding: '4px 10px', borderRadius: 20 }}>View List →</span>
              </div>
            </div>

            {/* 5. Total Pending Posts Card Button */}
            <div
              onClick={() => openCardModal('pending_posts')}
              style={{
                background: 'var(--card)',
                borderRadius: 20,
                padding: '24px 26px',
                border: '1px solid var(--line)',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.04)',
                borderTop: '5px solid #db2777',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
              className="cm-stat-clickable-card"
            >
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <span style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>PENDING POSTS</span>
                  <span style={{ background: 'var(--chip-bg)', padding: '10px', borderRadius: 14, border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconImage size={22} color="#db2777" />
                  </span>
                </div>
                <div style={{ fontSize: '2.8rem', fontWeight: 900, color: 'var(--ink)', lineHeight: 1 }}>{totalPendingPostsQty}</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 600 }}>Graphics pending</span>
                <span style={{ fontSize: '0.78rem', color: 'var(--brand)', fontWeight: 900, background: 'var(--chip-bg)', padding: '4px 10px', borderRadius: 20 }}>View List →</span>
              </div>
            </div>

            {/* 6. All Other Tasks Card Button */}
            <div
              onClick={() => openCardModal('other_tasks')}
              style={{
                background: 'var(--card)',
                borderRadius: 20,
                padding: '24px 26px',
                border: '1px solid var(--line)',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.04)',
                borderTop: '5px solid #224eae',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
              className="cm-stat-clickable-card"
            >
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <span style={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>OTHER TASKS</span>
                  <span style={{ background: '#f0f9ff', padding: '10px', borderRadius: 14, border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconFileText size={22} color="#224eae" />
                  </span>
                </div>
                <div style={{ fontSize: '2.8rem', fontWeight: 900, color: '#075985', lineHeight: 1 }}>{totalOtherTasksCount}</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--muted)', fontWeight: 600 }}>Ads & Web Dev</span>
                <span style={{ fontSize: '0.78rem', color: 'var(--brand)', fontWeight: 900, background: '#f0f9ff', padding: '4px 10px', borderRadius: 20 }}>View List →</span>
              </div>
            </div>
          </div>

          {/* ── Section: Service Renewal & Expiration Tracker (Full Width) ── */}
          {!isStaff && (
            <div
              style={{
                background: 'var(--card)',
                borderRadius: 24,
                padding: '28px 32px',
                border: '1px solid var(--line)',
                boxShadow: '0 10px 30px -5px rgba(0,0,0,0.04)',
                marginBottom: 36,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22, flexWrap: 'wrap', gap: 14 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 900, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <IconRenewal size={22} color="#224eae" /> Service Renewal & Expiration Tracker
                  </h3>
                  <p style={{ margin: '4px 0 0', fontSize: '0.88rem', color: 'var(--muted)' }}>
                    Real-time notification list for client renewals and expirations (Information Display)
                  </p>
                </div>

                {/* Filter Tabs */}
                <div style={{ display: 'flex', gap: 8, background: 'var(--bg)', padding: 4, borderRadius: 12 }}>
                  <button
                    type="button"
                    style={{
                      padding: '6px 14px',
                      borderRadius: 8,
                      border: 'none',
                      fontSize: '0.8rem',
                      fontWeight: 800,
                      cursor: 'pointer',
                      background: trackerFilter === 'all' ? '#ffffff' : 'transparent',
                      color: trackerFilter === 'all' ? '#0f172a' : '#64748b',
                      boxShadow: trackerFilter === 'all' ? '0 2px 6px rgba(0,0,0,0.06)' : 'none',
                    }}
                    onClick={() => setTrackerFilter('all')}
                  >
                    All ({processedTrackerItems.length})
                  </button>
                  <button
                    type="button"
                    style={{
                      padding: '6px 14px',
                      borderRadius: 8,
                      border: 'none',
                      fontSize: '0.8rem',
                      fontWeight: 800,
                      cursor: 'pointer',
                      background: trackerFilter === 'critical' ? '#ffffff' : 'transparent',
                      color: trackerFilter === 'critical' ? '#0f172a' : '#64748b',
                      boxShadow: trackerFilter === 'critical' ? '0 2px 6px rgba(0,0,0,0.06)' : 'none',
                    }}
                    onClick={() => setTrackerFilter('critical')}
                  >
                    Expired / Today
                  </button>
                  <button
                    type="button"
                    style={{
                      padding: '6px 14px',
                      borderRadius: 8,
                      border: 'none',
                      fontSize: '0.8rem',
                      fontWeight: 800,
                      cursor: 'pointer',
                      background: trackerFilter === 'upcoming' ? '#ffffff' : 'transparent',
                      color: trackerFilter === 'upcoming' ? '#224eae' : '#64748b',
                      boxShadow: trackerFilter === 'upcoming' ? '0 2px 6px rgba(0,0,0,0.06)' : 'none',
                    }}
                    onClick={() => setTrackerFilter('upcoming')}
                  >
                    Upcoming
                  </button>
                </div>
              </div>

              {filteredTrackerItems.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', background: 'var(--bg)', borderRadius: 16, fontSize: '0.95rem' }}>
                  <IconCheck size={20} /> No service renewals or expirations in this category.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {filteredTrackerItems.map((item, idx) => {
                    const level = item.exp.level;
                    const isCritical = level === 'critical';
                    const isWarning = level === 'warning';

                    const accentColor = isCritical ? '#ef4444' : isWarning ? '#f59e0b' : '#224eae';
                    const cardBg = isCritical ? '#f1f5f9' : isWarning ? '#f1f5f9' : '#f8fafc';
                    const cardBorder = isCritical ? '#cbd5e1' : isWarning ? '#cbd5e1' : '#e2e8f0';
                    const textColor = isCritical ? '#991b1b' : isWarning ? '#92400e' : '#0f172a';

                    return (
                      <div
                        key={idx}
                        style={{
                          background: cardBg,
                          border: `1px solid ${cardBorder}`,
                          borderLeft: `6px solid ${accentColor}`,
                          borderRadius: 14,
                          padding: '18px 24px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 14,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                          <div style={{ background: 'var(--card)', padding: 10, borderRadius: 12, border: `1px solid ${cardBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {isCritical ? (
                              <IconAlertTriangle size={22} color="#0f172a" />
                            ) : isWarning ? (
                              <IconClock size={22} color="#0f172a" />
                            ) : (
                              <IconCalendar size={22} color="#224eae" />
                            )}
                          </div>
                          <div>
                            {/* Exact format requested e.g. "parthbhai ashutosh 4 reels expire tomorrow" */}
                            <div style={{ fontSize: '1.08rem', fontWeight: 900, color: textColor, marginBottom: 4, letterSpacing: '-0.01em' }}>
                              {item.infoSentence}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                              Client Name: <strong>{item.clientFullName}</strong> · Service: <strong>{item.serviceName}</strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── 3 Detailed Breakdown Grid (Full Width 3 Columns) ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 24, marginBottom: 40 }}>
            {/* Pending Reels Breakdown */}
            <div style={{ background: 'var(--card)', borderRadius: 24, padding: '28px', border: '1px solid var(--line)', boxShadow: '0 10px 30px -5px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h4 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 900, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <IconFilm size={22} color="#224eae" /> Pending Reels
                </h4>
                <button
                  type="button"
                  onClick={() => openCardModal('pending_reels')}
                  style={{ background: 'var(--chip-bg)', color: '#6d28d9', border: '1px solid var(--line)', cursor: 'pointer', padding: '6px 14px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 900 }}
                >
                  {totalPendingReelsQty} Reels Total →
                </button>
              </div>

              {pendingReelsOrders.length === 0 && pendingReelsTasks.length === 0 ? (
                <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8', background: 'var(--bg)', borderRadius: 14, fontSize: '0.9rem' }}>
                  <IconCheck size={20} /> No pending Reels to produce!
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {pendingReelsOrders.map((ord) => {
                    const remaining = Math.max(1, (ord.quantity || 1) - (ord.completedQty || 0));
                    const nextQty = (ord.completedQty || 0) + 1;
                    const isLast = nextQty >= (ord.quantity || 1);

                    const handlePlusOne = async () => {
                      const newStatus = isLast ? 'Completed' : (ord.workStatus || 'In Progress');
                      try {
                        await api.put(`/api/client-module/clients/${ord.clientId}/orders/${ord.id}`, {
                          completedQty: nextQty,
                          workStatus: newStatus
                        });
                        loadData();
                      } catch (err) {
                        alert('Failed to update progress: ' + err.message);
                      }
                    };

                    const handleStatusChange = async (newStatus) => {
                      try {
                        await api.put(`/api/client-module/clients/${ord.clientId}/orders/${ord.id}`, {
                          workStatus: newStatus
                        });
                        loadData();
                      } catch (err) {
                        alert('Failed to update status: ' + err.message);
                      }
                    };

                    return (
                      <div key={ord.id} style={{ background: '#faf5ff', border: '1px solid var(--line)', borderRadius: 14, padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 900, color: '#4c1d95', fontSize: '0.98rem' }}>
                              {ord.clientName} {ord.company ? `(${ord.company})` : ''}
                            </div>
                            <div style={{ fontSize: '0.82rem', color: 'var(--brand)', marginTop: 3 }}>
                              {ord.serviceName}
                            </div>
                          </div>
                          <div style={{ background: '#224eae', color: 'var(--ink)', padding: '6px 14px', borderRadius: 20, fontWeight: 900, fontSize: '0.8rem' }}>
                            Done: {ord.completedQty || 0} / {ord.quantity || 1} ({remaining} Left)
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, borderTop: '1px dashed #e2e8f0', paddingTop: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 600 }}>Status:</span>
                            <select
                              value={ord.workStatus || 'In Progress'}
                              onChange={(e) => handleStatusChange(e.target.value)}
                              style={{ padding: '3px 6px', borderRadius: 8, border: '1px solid var(--line)', fontSize: '0.75rem', fontWeight: 700 }}
                            >
                              <option value="Waiting to Start">Waiting to Start</option>
                              <option value="In Progress">In Progress</option>
                              <option value="Waiting for Client">Waiting for Client</option>
                              <option value="On Hold">On Hold</option>
                              <option value="Completed">Completed</option>
                              <option value="Cancelled">Cancelled</option>
                            </select>
                          </div>
                          <button
                            type="button"
                            onClick={handlePlusOne}
                            style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '4px 10px', fontSize: '0.75rem', fontWeight: 900, cursor: 'pointer' }}
                          >
                            +1 Done
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pending Posts Breakdown */}
            <div style={{ background: 'var(--card)', borderRadius: 24, padding: '28px', border: '1px solid var(--line)', boxShadow: '0 10px 30px -5px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h4 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 900, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <IconImage size={22} color="#db2777" /> Pending Posts & Graphics
                </h4>
                <button
                  type="button"
                  onClick={() => openCardModal('pending_posts')}
                  style={{ background: 'var(--chip-bg)', color: '#be185d', border: '1px solid var(--line)', cursor: 'pointer', padding: '6px 14px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 900 }}
                >
                  {totalPendingPostsQty} Posts Total →
                </button>
              </div>

              {pendingPostsOrders.length === 0 && pendingPostsTasks.length === 0 ? (
                <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8', background: 'var(--bg)', borderRadius: 14, fontSize: '0.9rem' }}>
                  <IconCheck size={20} /> No pending posts or graphics!
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {pendingPostsOrders.map((ord) => {
                    const remaining = Math.max(1, (ord.quantity || 1) - (ord.completedQty || 0));
                    const nextQty = (ord.completedQty || 0) + 1;
                    const isLast = nextQty >= (ord.quantity || 1);

                    const handlePlusOne = async () => {
                      const newStatus = isLast ? 'Completed' : (ord.workStatus || 'In Progress');
                      try {
                        await api.put(`/api/client-module/clients/${ord.clientId}/orders/${ord.id}`, {
                          completedQty: nextQty,
                          workStatus: newStatus
                        });
                        loadData();
                      } catch (err) {
                        alert('Failed to update progress: ' + err.message);
                      }
                    };

                    const handleStatusChange = async (newStatus) => {
                      try {
                        await api.put(`/api/client-module/clients/${ord.clientId}/orders/${ord.id}`, {
                          workStatus: newStatus
                        });
                        loadData();
                      } catch (err) {
                        alert('Failed to update status: ' + err.message);
                      }
                    };

                    return (
                      <div key={ord.id} style={{ background: 'var(--chip-bg)', border: '1px solid var(--line)', borderRadius: 14, padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 900, color: '#831843', fontSize: '0.98rem' }}>
                              {ord.clientName} {ord.company ? `(${ord.company})` : ''}
                            </div>
                            <div style={{ fontSize: '0.82rem', color: 'var(--ink)', marginTop: 3 }}>
                              {ord.serviceName}
                            </div>
                          </div>
                          <div style={{ background: '#db2777', color: 'var(--ink)', padding: '6px 14px', borderRadius: 20, fontWeight: 900, fontSize: '0.8rem' }}>
                            Done: {ord.completedQty || 0} / {ord.quantity || 1} ({remaining} Left)
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, borderTop: '1px dashed #e2e8f0', paddingTop: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 600 }}>Status:</span>
                            <select
                              value={ord.workStatus || 'In Progress'}
                              onChange={(e) => handleStatusChange(e.target.value)}
                              style={{ padding: '3px 6px', borderRadius: 8, border: '1px solid var(--line)', fontSize: '0.75rem', fontWeight: 700 }}
                            >
                              <option value="Waiting to Start">Waiting to Start</option>
                              <option value="In Progress">In Progress</option>
                              <option value="Waiting for Client">Waiting for Client</option>
                              <option value="On Hold">On Hold</option>
                              <option value="Completed">Completed</option>
                              <option value="Cancelled">Cancelled</option>
                            </select>
                          </div>
                          <button
                            type="button"
                            onClick={handlePlusOne}
                            style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '4px 10px', fontSize: '0.75rem', fontWeight: 900, cursor: 'pointer' }}
                          >
                            +1 Done
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* All Other Pending Tasks Breakdown */}
            <div style={{ background: 'var(--card)', borderRadius: 24, padding: '28px', border: '1px solid var(--line)', boxShadow: '0 10px 30px -5px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h4 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 900, color: '#075985', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <IconFileText size={22} color="#224eae" /> All Other Pending Tasks
                </h4>
                <button
                  type="button"
                  onClick={() => openCardModal('other_tasks')}
                  style={{ background: '#f0f9ff', color: 'var(--brand)', border: '1px solid var(--line)', cursor: 'pointer', padding: '6px 14px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 900 }}
                >
                  {totalOtherTasksCount} Items →
                </button>
              </div>

              {otherPendingOrders.length === 0 && otherPendingTasks.length === 0 ? (
                <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8', background: 'var(--bg)', borderRadius: 14, fontSize: '0.9rem' }}>
                  <IconCheck size={20} /> All other tasks completed!
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {otherPendingOrders.map((ord) => {
                    const remaining = Math.max(1, (ord.quantity || 1) - (ord.completedQty || 0));
                    const nextQty = (ord.completedQty || 0) + 1;
                    const isLast = nextQty >= (ord.quantity || 1);

                    const handlePlusOne = async () => {
                      const newStatus = isLast ? 'Completed' : (ord.workStatus || 'In Progress');
                      try {
                        await api.put(`/api/client-module/clients/${ord.clientId}/orders/${ord.id}`, {
                          completedQty: nextQty,
                          workStatus: newStatus
                        });
                        loadData();
                      } catch (err) {
                        alert('Failed to update progress: ' + err.message);
                      }
                    };

                    const handleStatusChange = async (newStatus) => {
                      try {
                        await api.put(`/api/client-module/clients/${ord.clientId}/orders/${ord.id}`, {
                          workStatus: newStatus
                        });
                        loadData();
                      } catch (err) {
                        alert('Failed to update status: ' + err.message);
                      }
                    };

                    return (
                      <div key={ord.id} style={{ background: '#f0f9ff', border: '1px solid var(--line)', borderRadius: 14, padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 900, color: '#0c4a6e', fontSize: '0.98rem' }}>
                              {ord.clientName} {ord.company ? `(${ord.company})` : ''}
                            </div>
                            <div style={{ fontSize: '0.82rem', color: 'var(--brand)', marginTop: 3 }}>
                              {ord.serviceName}
                            </div>
                          </div>
                          <div style={{ background: '#224eae', color: 'var(--ink)', padding: '6px 14px', borderRadius: 20, fontWeight: 900, fontSize: '0.8rem' }}>
                            Done: {ord.completedQty || 0} / {ord.quantity || 1} ({remaining} Left)
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, borderTop: '1px dashed #e2e8f0', paddingTop: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 600 }}>Status:</span>
                            <select
                              value={ord.workStatus || 'In Progress'}
                              onChange={(e) => handleStatusChange(e.target.value)}
                              style={{ padding: '3px 6px', borderRadius: 8, border: '1px solid var(--line)', fontSize: '0.75rem', fontWeight: 700 }}
                            >
                              <option value="Waiting to Start">Waiting to Start</option>
                              <option value="In Progress">In Progress</option>
                              <option value="Waiting for Client">Waiting for Client</option>
                              <option value="On Hold">On Hold</option>
                              <option value="Completed">Completed</option>
                              <option value="Cancelled">Cancelled</option>
                            </select>
                          </div>
                          <button
                            type="button"
                            onClick={handlePlusOne}
                            style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '4px 10px', fontSize: '0.75rem', fontWeight: 900, cursor: 'pointer' }}
                          >
                            +1 Done
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {otherPendingOrders.length === 0 && otherPendingTasks.map((t) => (
                    <div key={t.id} style={{ background: '#f0f9ff', border: '1px solid var(--line)', borderRadius: 14, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 900, color: '#0c4a6e', fontSize: '0.98rem' }}>{t.clientName}</div>
                        <div style={{ fontSize: '0.82rem', color: 'var(--brand)', marginTop: 3 }}>{t.taskName}</div>
                      </div>
                      <div style={{ background: '#224eae', color: 'var(--ink)', padding: '6px 14px', borderRadius: 20, fontWeight: 900, fontSize: '0.8rem' }}>
                        {t.dueDate ? fmtDDMMYYYY(t.dueDate) : 'Pending'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── INTERACTIVE POPUP MODALS FOR STAT CARDS ── */}

      {/* 1. Modal: Total Clients List */}
      {activeModal === 'clients' && (
        <CrmModal
          onClose={() => setActiveModal(null)}
          title="Total Registered Clients"
          subtitle={`Complete directory of ${clients.length} registered clients`}
          maxWidth={920}
        >
          <div style={{ marginBottom: 16 }}>
            <input
              type="text"
              placeholder="Search client by name, company, mobile..."
              value={modalSearch}
              onChange={(e) => setModalSearch(e.target.value)}
              style={{ width: '100%', padding: '12px 16px', borderRadius: 12, border: '1px solid var(--line)', fontSize: '0.95rem' }}
            />
          </div>
          <div style={{ overflowX: 'auto', maxHeight: '60vh' }}>
            <table className="cm-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--line)', textAlign: 'left' }}>
                  <th style={{ padding: '12px 16px' }}>#</th>
                  <th style={{ padding: '12px 16px' }}>Client Name</th>
                  <th style={{ padding: '12px 16px' }}>Company</th>
                  <th style={{ padding: '12px 16px' }}>Mobile</th>
                  <th style={{ padding: '12px 16px' }}>Status</th>
                  <th style={{ padding: '12px 16px' }}>Join Date</th>
                </tr>
              </thead>
              <tbody>
                {clients
                  .filter((c) => {
                    if (!modalSearch.trim()) return true;
                    const s = modalSearch.toLowerCase();
                    return (
                      String(c.clientName || '').toLowerCase().includes(s) ||
                      String(c.company || '').toLowerCase().includes(s) ||
                      String(c.mobile || '').toLowerCase().includes(s)
                    );
                  })
                  .map((c, idx) => (
                    <tr key={c.id || idx} style={{ borderBottom: '1px solid var(--line)' }}>
                      <td style={{ padding: '14px 16px', color: 'var(--muted)' }}>{idx + 1}</td>
                      <td style={{ padding: '14px 16px', fontWeight: 900, color: 'var(--ink)' }}>{c.clientName}</td>
                      <td style={{ padding: '14px 16px', color: 'var(--muted)' }}>{c.company || '—'}</td>
                      <td style={{ padding: '14px 16px', color: 'var(--muted)' }}>{c.mobile || '—'}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ background: c.status === 'Inactive' ? '#fee2e2' : '#f0f4ff', color: c.status === 'Inactive' ? '#991b1b' : '#1c3d8a', padding: '6px 14px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 900 }}>
                          {c.status || 'Active'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', color: 'var(--muted)' }}>{c.joinDate ? fmtDDMMYYYY(c.joinDate) : '—'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </CrmModal>
      )}

      {/* 2. Modal: Total Active Services List */}
      {activeModal === 'active_services' && (
        <CrmModal
          onClose={() => setActiveModal(null)}
          title="Active Services & Orders"
          subtitle={`All ${activeOrders.length} active service subscriptions`}
          maxWidth={960}
        >
          <div style={{ marginBottom: 16 }}>
            <input
              type="text"
              placeholder="Search service by client or service name..."
              value={modalSearch}
              onChange={(e) => setModalSearch(e.target.value)}
              style={{ width: '100%', padding: '12px 16px', borderRadius: 12, border: '1px solid var(--line)', fontSize: '0.95rem' }}
            />
          </div>
          <div style={{ overflowX: 'auto', maxHeight: '60vh' }}>
            <table className="cm-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--line)', textAlign: 'left' }}>
                  <th style={{ padding: '12px 16px' }}>#</th>
                  <th style={{ padding: '12px 16px' }}>Client</th>
                  <th style={{ padding: '12px 16px' }}>Service Name</th>
                  <th style={{ padding: '12px 16px' }}>Quantity</th>
                  <th style={{ padding: '12px 16px' }}>Status</th>
                  <th style={{ padding: '12px 16px' }}>Renewal Date</th>
                </tr>
              </thead>
              <tbody>
                {activeOrders
                  .filter((ord) => {
                    if (!modalSearch.trim()) return true;
                    const s = modalSearch.toLowerCase();
                    return (
                      String(ord.clientName || '').toLowerCase().includes(s) ||
                      String(ord.company || '').toLowerCase().includes(s) ||
                      String(ord.serviceName || '').toLowerCase().includes(s)
                    );
                  })
                  .map((ord, idx) => (
                    <tr key={ord.id || idx} style={{ borderBottom: '1px solid var(--line)' }}>
                      <td style={{ padding: '14px 16px', color: 'var(--muted)' }}>{idx + 1}</td>
                      <td style={{ padding: '14px 16px', fontWeight: 900, color: 'var(--ink)' }}>
                        {ord.clientName} {ord.company ? `(${ord.company})` : ''}
                      </td>
                      <td style={{ padding: '14px 16px', color: 'var(--brand)', fontWeight: 800 }}>{ord.serviceName}</td>
                      <td style={{ padding: '14px 16px', color: 'var(--muted)', fontWeight: 900 }}>{ord.quantity} {ord.unit || ''}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ background: 'var(--chip-bg)', color: 'var(--brand)', padding: '6px 14px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 900 }}>
                          {ord.workStatus || 'In Progress'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', color: 'var(--muted)', fontWeight: 800 }}>
                        {ord.renewalDate ? fmtDDMMYYYY(ord.renewalDate) : '—'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </CrmModal>
      )}

      {/* 3. Modal: Pending All Work List */}
      {activeModal === 'pending_all' && (
        <CrmModal
          onClose={() => setActiveModal(null)}
          title="Pending Work & Tasks"
          subtitle={`List of all ${totalPendingWorkCount} work items in progress`}
          maxWidth={960}
        >
          <div style={{ marginBottom: 16 }}>
            <input
              type="text"
              placeholder="Search work item by client or service name..."
              value={modalSearch}
              onChange={(e) => setModalSearch(e.target.value)}
              style={{ width: '100%', padding: '12px 16px', borderRadius: 12, border: '1px solid var(--line)', fontSize: '0.95rem' }}
            />
          </div>
          <div style={{ overflowX: 'auto', maxHeight: '60vh' }}>
            <table className="cm-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--line)', textAlign: 'left' }}>
                  <th style={{ padding: '12px 16px' }}>#</th>
                  <th style={{ padding: '12px 16px' }}>Client</th>
                  <th style={{ padding: '12px 16px' }}>Service / Task</th>
                  <th style={{ padding: '12px 16px' }}>Work Status</th>
                  <th style={{ padding: '12px 16px' }}>Target Date</th>
                </tr>
              </thead>
              <tbody>
                {pendingOrders
                  .filter((ord) => {
                    if (!modalSearch.trim()) return true;
                    const s = modalSearch.toLowerCase();
                    return (
                      String(ord.clientName || '').toLowerCase().includes(s) ||
                      String(ord.company || '').toLowerCase().includes(s) ||
                      String(ord.serviceName || '').toLowerCase().includes(s)
                    );
                  })
                  .map((ord, idx) => (
                    <tr key={ord.id || idx} style={{ borderBottom: '1px solid var(--line)' }}>
                      <td style={{ padding: '14px 16px', color: 'var(--muted)' }}>{idx + 1}</td>
                      <td style={{ padding: '14px 16px', fontWeight: 900, color: 'var(--ink)' }}>
                        {ord.clientName} {ord.company ? `(${ord.company})` : ''}
                      </td>
                      <td style={{ padding: '14px 16px', color: 'var(--ink)', fontWeight: 800 }}>{ord.serviceName} (Done: {ord.completedQty || 0} / {ord.quantity || 1} {ord.unit})</td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ background: 'var(--bg)', color: 'var(--ink)', padding: '6px 14px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 900 }}>
                          {ord.workStatus || 'In Progress'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', color: 'var(--muted)', fontWeight: 800 }}>
                        {ord.renewalDate ? fmtDDMMYYYY(ord.renewalDate) : '—'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </CrmModal>
      )}

      {/* 4. Modal: Total Pending Reels List */}
      {activeModal === 'pending_reels' && (
        <CrmModal
          onClose={() => setActiveModal(null)}
          title="Pending Reels List"
          subtitle={`All clients with ${totalPendingReelsQty} pending reels`}
          maxWidth={920}
        >
          <div style={{ marginBottom: 16 }}>
            <input
              type="text"
              placeholder="Search client by name..."
              value={modalSearch}
              onChange={(e) => setModalSearch(e.target.value)}
              style={{ width: '100%', padding: '12px 16px', borderRadius: 12, border: '1px solid var(--line)', fontSize: '0.95rem' }}
            />
          </div>
          <div style={{ overflowX: 'auto', maxHeight: '60vh' }}>
            <table className="cm-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--line)', textAlign: 'left' }}>
                  <th style={{ padding: '12px 16px' }}>#</th>
                  <th style={{ padding: '12px 16px' }}>Client</th>
                  <th style={{ padding: '12px 16px' }}>Service</th>
                  <th style={{ padding: '12px 16px' }}>Pending Reels</th>
                  <th style={{ padding: '12px 16px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pendingReelsOrders
                  .filter((ord) => {
                    if (!modalSearch.trim()) return true;
                    const s = modalSearch.toLowerCase();
                    return (
                      String(ord.clientName || '').toLowerCase().includes(s) ||
                      String(ord.company || '').toLowerCase().includes(s)
                    );
                  })
                  .map((ord, idx) => {
                    const remaining = Math.max(1, (ord.quantity || 1) - (ord.completedQty || 0));
                    return (
                      <tr key={ord.id || idx} style={{ borderBottom: '1px solid var(--line)' }}>
                        <td style={{ padding: '14px 16px', color: 'var(--muted)' }}>{idx + 1}</td>
                        <td style={{ padding: '14px 16px', fontWeight: 900, color: 'var(--ink)' }}>
                          {ord.clientName} {ord.company ? `(${ord.company})` : ''}
                        </td>
                        <td style={{ padding: '14px 16px', color: 'var(--brand)', fontWeight: 800 }}>{ord.serviceName}</td>
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{ background: '#224eae', color: 'var(--ink)', padding: '6px 14px', borderRadius: 20, fontWeight: 900, fontSize: '0.84rem' }}>
                            Done: {ord.completedQty || 0} / {ord.quantity || 1} ({remaining} Left)
                          </span>
                        </td>
                        <td style={{ padding: '14px 16px', color: 'var(--muted)' }}>{ord.workStatus || 'In Progress'}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </CrmModal>
      )}

      {/* 5. Modal: Total Pending Posts List */}
      {activeModal === 'pending_posts' && (
        <CrmModal
          onClose={() => setActiveModal(null)}
          title="Pending Posts & Graphics"
          subtitle={`All clients with ${totalPendingPostsQty} pending posts`}
          maxWidth={920}
        >
          <div style={{ marginBottom: 16 }}>
            <input
              type="text"
              placeholder="Search client by name..."
              value={modalSearch}
              onChange={(e) => setModalSearch(e.target.value)}
              style={{ width: '100%', padding: '12px 16px', borderRadius: 12, border: '1px solid var(--line)', fontSize: '0.95rem' }}
            />
          </div>
          <div style={{ overflowX: 'auto', maxHeight: '60vh' }}>
            <table className="cm-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--line)', textAlign: 'left' }}>
                  <th style={{ padding: '12px 16px' }}>#</th>
                  <th style={{ padding: '12px 16px' }}>Client</th>
                  <th style={{ padding: '12px 16px' }}>Service</th>
                  <th style={{ padding: '12px 16px' }}>Pending Posts</th>
                  <th style={{ padding: '12px 16px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pendingPostsOrders.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>
                      <IconCheck size={20} /> No pending posts or graphics!
                    </td>
                  </tr>
                ) : (
                  pendingPostsOrders
                    .filter((ord) => {
                      if (!modalSearch.trim()) return true;
                      const s = modalSearch.toLowerCase();
                      return (
                        String(ord.clientName || '').toLowerCase().includes(s) ||
                        String(ord.company || '').toLowerCase().includes(s)
                      );
                    })
                    .map((ord, idx) => {
                      const remaining = Math.max(1, (ord.quantity || 1) - (ord.completedQty || 0));
                      return (
                        <tr key={ord.id || idx} style={{ borderBottom: '1px solid var(--line)' }}>
                          <td style={{ padding: '14px 16px', color: 'var(--muted)' }}>{idx + 1}</td>
                          <td style={{ padding: '14px 16px', fontWeight: 900, color: 'var(--ink)' }}>
                            {ord.clientName} {ord.company ? `(${ord.company})` : ''}
                          </td>
                          <td style={{ padding: '14px 16px', color: 'var(--brand)', fontWeight: 800 }}>{ord.serviceName}</td>
                          <td style={{ padding: '14px 16px' }}>
                            <span style={{ background: '#db2777', color: 'var(--ink)', padding: '6px 14px', borderRadius: 20, fontWeight: 900, fontSize: '0.84rem' }}>
                              Done: {ord.completedQty || 0} / {ord.quantity || 1} ({remaining} Left)
                            </span>
                          </td>
                          <td style={{ padding: '14px 16px', color: 'var(--muted)' }}>{ord.workStatus || 'In Progress'}</td>
                        </tr>
                      );
                    })
                )}
              </tbody>
            </table>
          </div>
        </CrmModal>
      )}

      {/* 6. Modal: All Other Tasks List */}
      {activeModal === 'other_tasks' && (
        <CrmModal
          onClose={() => setActiveModal(null)}
          title="All Other Pending Tasks"
          subtitle={`Non-reels, non-posts ${totalOtherTasksCount} pending items`}
          maxWidth={920}
        >
          <div style={{ marginBottom: 16 }}>
            <input
              type="text"
              placeholder="Search client or service..."
              value={modalSearch}
              onChange={(e) => setModalSearch(e.target.value)}
              style={{ width: '100%', padding: '12px 16px', borderRadius: 12, border: '1px solid var(--line)', fontSize: '0.95rem' }}
            />
          </div>
          <div style={{ overflowX: 'auto', maxHeight: '60vh' }}>
            <table className="cm-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--line)', textAlign: 'left' }}>
                  <th style={{ padding: '12px 16px' }}>#</th>
                  <th style={{ padding: '12px 16px' }}>Client</th>
                  <th style={{ padding: '12px 16px' }}>Service Name</th>
                  <th style={{ padding: '12px 16px' }}>Status</th>
                  <th style={{ padding: '12px 16px' }}>Target Date</th>
                </tr>
              </thead>
              <tbody>
                {otherPendingOrders
                  .filter((ord) => {
                    if (!modalSearch.trim()) return true;
                    const s = modalSearch.toLowerCase();
                    return (
                      String(ord.clientName || '').toLowerCase().includes(s) ||
                      String(ord.company || '').toLowerCase().includes(s) ||
                      String(ord.serviceName || '').toLowerCase().includes(s)
                    );
                  })
                  .map((ord, idx) => (
                    <tr key={ord.id || idx} style={{ borderBottom: '1px solid var(--line)' }}>
                      <td style={{ padding: '14px 16px', color: 'var(--muted)' }}>{idx + 1}</td>
                      <td style={{ padding: '14px 16px', fontWeight: 900, color: 'var(--ink)' }}>
                        {ord.clientName} {ord.company ? `(${ord.company})` : ''}
                      </td>
                      <td style={{ padding: '14px 16px', color: 'var(--brand)', fontWeight: 800 }}>{ord.serviceName} (Done: {ord.completedQty || 0} / {ord.quantity || 1})</td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ background: '#f0f9ff', color: 'var(--brand)', padding: '6px 14px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 900 }}>
                          {ord.workStatus || 'In Progress'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', color: 'var(--muted)', fontWeight: 800 }}>
                        {ord.renewalDate ? fmtDDMMYYYY(ord.renewalDate) : '—'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </CrmModal>
      )}
    </div>
  );
}
